/**
 * t378u / t385u: The Hub's one plan, billing states and the rules the control plane and the main API both apply. Pure TS.
 *
 * One plan with everything in it (t385u, Daniel's decision), GBP, monthly only, all prices exclusive of VAT:
 *  - The Hub: £25 a month covers up to 5 users; each user beyond 5 is £3 a month (a graduated Stripe price).
 *  - AI assistant (optional add-on): £1 per user per month on every user in the workspace while it's switched on.
 * The free trial includes everything, the AI assistant too.
 */

export const HUB_PRICING = {
  currency: 'gbp',
  interval: 'month',
  /** Users the base price covers. */
  includedUsers: 5,
  /** £25 a month for up to includedUsers. */
  basePence: 2500,
  /** Each user beyond includedUsers. */
  extraUserPence: 300,
  /** The AI assistant add-on, per user per month. */
  aiUserPence: 100,
} as const;

/** Stripe objects (fixed product ids; prices found by lookup_key). Bump the version to replace a price. */
export const HUB_PLAN_PRODUCT = 'hub_plan';
export const HUB_AI_PRODUCT = 'hub_ai';
export const PRICE_VERSION = 'v1';
export const HUB_PLAN_LOOKUP_KEY = `hub_plan_monthly_${PRICE_VERSION}`;
export const HUB_AI_LOOKUP_KEY = `hub_ai_monthly_${PRICE_VERSION}`;
/** The three placeholder tiers this replaced (archived in Stripe, never deleted). */
export const LEGACY_PLAN_PRODUCTS = ['hub_essentials', 'hub_workforce', 'hub_complete'];
export const HUB_PLAN_NAME = 'The Hub';
export const HUB_AI_NAME = 'AI assistant';
/** The only plan key a workspace can have (collab.workspace_billing.plan). */
export type PlanKey = 'hub';
export type BillingInterval = 'month';

/** Which line a Stripe price is: the plan, the AI add-on, or neither (an archived placeholder tier). */
export function lineForLookupKey(key: string | null | undefined): 'plan' | 'ai' | null {
  if (!key) return null;
  if (/^hub_plan_monthly_v\d+$/.test(key)) return 'plan';
  if (/^hub_ai_monthly_v\d+$/.test(key)) return 'ai';
  return null;
}

const users = (n: number) => Math.max(0, Math.floor(Number.isFinite(n) ? n : 0));
/** The plan's monthly price in pence for `n` users: £25 up to 5, then £3 for each extra user. */
export const planPence = (n: number) => HUB_PRICING.basePence + Math.max(0, users(n) - HUB_PRICING.includedUsers) * HUB_PRICING.extraUserPence;
/** The AI assistant's monthly price in pence for `n` users. */
export const aiPence = (n: number) => users(n) * HUB_PRICING.aiUserPence;
/** Monthly total in pence (ex VAT). */
export const monthlyPence = (n: number, ai: boolean) => planPence(n) + (ai ? aiPence(n) : 0);

/** £25, £34, £2.50 (whole pounds without pence). */
export const gbp = (pence: number) => `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: pence % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
const userWord = (n: number) => `${n} ${n === 1 ? 'user' : 'users'}`;

export interface PriceLine { key: 'plan' | 'ai'; label: string; users: number; pence: number; /** "The Hub, 8 users: £25 for the first 5 + 3 × £3 = £34/month" */ text: string }
export interface PriceBreakdown { lines: PriceLine[]; total_pence: number; /** "£42/month + VAT" */ total_text: string }

/**
 * The monthly breakdown shown on the Billing page, the site and the brochure, ex VAT:
 *   The Hub, 8 users: £25 for the first 5 + 3 × £3 = £34/month
 *   AI assistant, 8 users: £8/month
 */
export function priceBreakdown(n: number, ai: boolean): PriceBreakdown {
  const u = Math.max(1, users(n));
  const extra = Math.max(0, u - HUB_PRICING.includedUsers);
  const plan = planPence(u);
  const planText = extra
    ? `${HUB_PLAN_NAME}, ${userWord(u)}: ${gbp(HUB_PRICING.basePence)} for the first ${HUB_PRICING.includedUsers} + ${extra} × ${gbp(HUB_PRICING.extraUserPence)} = ${gbp(plan)}/month`
    : `${HUB_PLAN_NAME}, ${userWord(u)}: ${gbp(plan)}/month (covers up to ${HUB_PRICING.includedUsers} users)`;
  const lines: PriceLine[] = [{ key: 'plan', label: HUB_PLAN_NAME, users: u, pence: plan, text: planText }];
  if (ai) lines.push({ key: 'ai', label: HUB_AI_NAME, users: u, pence: aiPence(u), text: `${HUB_AI_NAME}, ${userWord(u)}: ${gbp(aiPence(u))}/month` });
  const total = lines.reduce((a, l) => a + l.pence, 0);
  return { lines, total_pence: total, total_text: `${gbp(total)}/month + VAT` };
}

/** Defaults; each is configurable in the control plane's env (docs/saas-control-plane.md). */
export const SAAS_DEFAULTS = {
  trialDays: 14,
  /** Reminder emails this many days before the trial ends. */
  trialReminderDays: [7, 3, 1] as number[],
  /** past_due → read-only after this many days of failed payment (the plan Page: day 15). */
  pastDueGraceDays: 14,
  /** Data kept this long after cancellation or trial expiry, then flagged for purge (purge itself not built). */
  retentionDays: 30,
  /** Instant demos expire after this many days. */
  demoDays: 7,
  /** Seat changes are sent to Stripe once nothing has changed for this long. */
  seatSyncDebounceMinutes: 10,
};

/**
 * Tenant (control plane) and workspace (main API) billing status:
 *  - pending: signed up, email not verified (control plane only; no workspace yet)
 *  - trialing: free trial, no card
 *  - active: paying
 *  - past_due: a payment failed; banner, still fully usable until the grace period ends
 *  - read_only: trial ended unconverted, or past_due beyond grace; viewable and exportable, nothing new
 *  - cancelled: subscription ended; read-only, data kept for the retention period
 */
export type BillingStatus = 'pending' | 'trialing' | 'active' | 'past_due' | 'read_only' | 'cancelled';
export const BILLING_STATUSES: BillingStatus[] = ['pending', 'trialing', 'active', 'past_due', 'read_only', 'cancelled'];
/** Who bills the workspace: Stripe self-serve, an enterprise contract (no Stripe, never read-only from billing) or a demo. */
export type BillingMode = 'self_serve' | 'enterprise' | 'demo';

export interface BillingRecord {
  mode: BillingMode;
  status: BillingStatus;
  trial_ends_at?: string | null;
  past_due_since?: string | null;
  cancelled_at?: string | null;
  read_only_since?: string | null;
  read_only_reason?: ReadOnlyReason | null;
  demo_expires_at?: string | null;
}

export type ReadOnlyReason = 'trial_ended' | 'payment_failed' | 'cancelled' | 'demo_expired' | 'manual';
export interface BillingState {
  mode: BillingMode;
  /** After time-based transitions (an expired trial is read_only even before the control plane's job runs). */
  status: BillingStatus;
  readOnly: boolean;
  reason: ReadOnlyReason | null;
  /** Whole days left in the trial (rounded up), or null when not trialing. */
  trialDaysLeft: number | null;
  /** When a past_due workspace becomes read-only. */
  graceEndsAt: string | null;
  /** When data may be purged (retention after cancellation / expiry), or null. */
  purgeAfter: string | null;
}

const DAY = 86_400_000;
const t = (s?: string | null) => (s ? Date.parse(s) : NaN);
const iso = (ms: number) => new Date(ms).toISOString();

/**
 * The effective state at `now`. No row / enterprise mode → never read-only from billing (Trade Outlet and the
 * existing demo workspace are 'enterprise / no billing').
 */
export function billingStateAt(rec: BillingRecord | null | undefined, now: Date = new Date(), opts: { pastDueGraceDays?: number; retentionDays?: number } = {}): BillingState {
  const grace = (opts.pastDueGraceDays ?? SAAS_DEFAULTS.pastDueGraceDays) * DAY;
  const keep = (opts.retentionDays ?? SAAS_DEFAULTS.retentionDays) * DAY;
  const n = now.getTime();
  const base: BillingState = { mode: rec?.mode ?? 'enterprise', status: rec?.status ?? 'active', readOnly: false, reason: null, trialDaysLeft: null, graceEndsAt: null, purgeAfter: null };
  if (!rec || rec.mode === 'enterprise') return { ...base, mode: 'enterprise', status: 'active' };
  if (rec.mode === 'demo') {
    const exp = t(rec.demo_expires_at);
    return Number.isFinite(exp) && n >= exp ? { ...base, status: 'read_only', readOnly: true, reason: 'demo_expired' } : { ...base, status: 'active' };
  }
  switch (rec.status) {
    case 'pending':
    case 'active':
      return base;
    case 'trialing': {
      const end = t(rec.trial_ends_at);
      if (Number.isFinite(end) && n >= end) return { ...base, status: 'read_only', readOnly: true, reason: 'trial_ended', purgeAfter: iso(end + keep) };
      return { ...base, trialDaysLeft: Number.isFinite(end) ? Math.max(0, Math.ceil((end - n) / DAY)) : null };
    }
    case 'past_due': {
      const since = t(rec.past_due_since);
      const ends = Number.isFinite(since) ? since + grace : NaN;
      if (Number.isFinite(ends) && n >= ends) return { ...base, status: 'read_only', readOnly: true, reason: 'payment_failed', graceEndsAt: iso(ends) };
      return { ...base, graceEndsAt: Number.isFinite(ends) ? iso(ends) : null };
    }
    case 'read_only': {
      const since = t(rec.read_only_since);
      const reason: ReadOnlyReason = rec.read_only_reason ?? (rec.past_due_since ? 'payment_failed' : 'manual');
      return { ...base, readOnly: true, reason, purgeAfter: Number.isFinite(since) ? iso(since + keep) : null };
    }
    case 'cancelled': {
      const at = t(rec.cancelled_at);
      return { ...base, readOnly: true, reason: 'cancelled', purgeAfter: Number.isFinite(at) ? iso(at + keep) : null };
    }
  }
}

/** Stripe subscription status → our status. */
export function statusFromStripe(s: string): BillingStatus {
  switch (s) {
    case 'active': return 'active';
    case 'trialing': return 'active'; // we don't use Stripe trials (the trial is ours, no card); treat as paid
    case 'past_due': return 'past_due';
    case 'unpaid': return 'read_only';
    case 'canceled': case 'incomplete_expired': return 'cancelled';
    case 'incomplete': case 'paused': default: return 'past_due';
  }
}

/** Trial reminder due today? `sent` is the list of day-marks already sent (7, 3, 1). */
export function dueTrialReminder(trialEndsAt: string, sent: number[], now: Date = new Date(), marks: number[] = SAAS_DEFAULTS.trialReminderDays): number | null {
  const left = (Date.parse(trialEndsAt) - now.getTime()) / DAY;
  if (!(left > 0)) return null;
  // The smallest mark we've reached that isn't sent yet (a sleeping job that wakes at 2 days left sends "3", not "7").
  const reached = [...marks].sort((a, b) => a - b).filter(m => left <= m && !sent.includes(m));
  return reached.length ? reached[0] : null;
}

/**
 * Paths a read-only workspace still allows (non-GET). `path` is after /v1. Clocking in and out, timesheets and
 * exports keep working so wages are never held up (plan Page, "When a customer doesn't pay"); signing in and out,
 * your own account, notifications, push registration, search and the billing endpoints are always allowed.
 */
const ALWAYS = [/^\/me(\/|$)/, /^\/security(\/|$)/, /^\/sign-ins(\/|$)/, /^\/notifications(\/|$)/, /^\/push(\/|$)/, /^\/presence(\/|$)/, /^\/dev(\/|$)/, /^\/internal(\/|$)/, /^\/branding(\/|$)/, /^\/search(\/|$)/, /^\/kiosk(\/|$)/,
  /^\/conversations\/[^/]+\/read$/, /^\/incidents\/read$/];
const WS_ALLOWED = [/^billing(\/|$)/, /^workforce\/time(\/|$)/, /^workforce\/timesheets(\/|$)/, /^workforce\/export(\/|$)/, /^workforce\/kiosk-events(\/|$)/, /^workforce\/attendance(\/|$)/, /^exports?(\/|$)/, /(^|\/)export(\/|$)/, /(^|\/)search(\/|$)/, /^notifications(\/|$)/, /^nav(\/|$)/, /^me(\/|$)/,
  /^workforce\/kiosk-credentials\/me(\/|$)/, /(^|\/)read(-all|-entity)?$/, /^settings\/prefs(\/|$)/];

export function readOnlyAllows(method: string, rawPath: string): boolean {
  const m = method.toUpperCase();
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return true;
  const path = rawPath.replace(/^\/v1(?=\/)/, '').split('?')[0];
  if (ALWAYS.some(r => r.test(path))) return true;
  const ws = /^\/workspaces\/[0-9a-f-]{36}\/(.+)$/i.exec(path);
  if (ws) return WS_ALLOWED.some(r => r.test(ws[1]));
  return false;
}

/** The workspace's billing as the app sees it (GET /workspaces/:ws/billing). */
export interface WorkspaceBillingView {
  mode: BillingMode;
  status: BillingStatus;
  read_only: boolean;
  reason: ReadOnlyReason | null;
  trial_ends_at: string | null;
  trial_days_left: number | null;
  grace_ends_at: string | null;
  /** Owners and Admins only (null for others). */
  details: null | {
    plan: PlanKey | null;
    plan_name: string;
    /** Paying now (active or past_due with a subscription): AI is switched on or off here, everything else in the portal. */
    subscribed: boolean;
    /** Users on the subscription (Stripe quantity), or null before checkout. */
    seats: number | null;
    billable_users: number;
    /** The AI assistant add-on is on (always on during the trial). */
    ai_enabled: boolean;
    /** What a month costs: for `seats` when subscribed, otherwise for the billable users now (with AI as it is now). */
    breakdown: PriceBreakdown;
    /** The same with AI the other way round, so the page can say what switching costs. */
    breakdown_other_ai: PriceBreakdown;
    next_invoice_at: string | null;
    next_invoice_pence: number | null;
    currency: string;
    cancel_at_period_end: boolean;
    /** When a scheduled cancellation takes effect (null unless cancel_at_period_end). */
    ends_at: string | null;
    can_manage: boolean;
  };
}

/** Banner to show (Owner and Admin only), or null. */
export function billingBanner(v: Pick<WorkspaceBillingView, 'mode' | 'status' | 'read_only' | 'reason' | 'trial_days_left' | 'grace_ends_at'> | null | undefined):
  null | { tone: 'info' | 'warning' | 'danger'; text: string; action: 'subscribe' | 'update_card' } {
  if (!v || v.mode !== 'self_serve') return null;
  if (v.read_only) {
    const why = v.reason === 'trial_ended' ? 'Your free trial has ended' : v.reason === 'payment_failed' ? 'Payment failed' : v.reason === 'cancelled' ? 'Your subscription has ended' : 'This workspace is read-only';
    return { tone: 'danger', text: `${why}: the workspace is read-only. You can still view and export everything.`, action: v.reason === 'payment_failed' ? 'update_card' : 'subscribe' };
  }
  if (v.status === 'trialing' && v.trial_days_left !== null) {
    const d = v.trial_days_left;
    return { tone: d <= 3 ? 'warning' : 'info', text: d <= 0 ? 'Your free trial ends today.' : `${d} ${d === 1 ? 'day' : 'days'} left in your free trial.`, action: 'subscribe' };
  }
  if (v.status === 'past_due') {
    const when = v.grace_ends_at ? new Date(v.grace_ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'Europe/London' }) : null;
    return { tone: 'warning', text: `We couldn’t take your last payment.${when ? ` Update your card by ${when} to keep full access.` : ''}`, action: 'update_card' };
  }
  return null;
}

/** Who sees the billing page and banner and can start checkout: Owners and Admins (the same key as Settings › Modules). */
export const BILLING_PERMISSION = 'admin.roles.manage';
